import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { CheckCircle, BookOpen, Target, Users, Zap, Star, ArrowRight } from "lucide-react";

export const WhyLearnSection = () => {
  const cards = [
    { icon: <Target size={28} />, title: "Industry-Ready Curriculum", text: "Learn the exact tools, languages, and frameworks engineering teams at top global companies are using right now." },
    { icon: <Zap size={28} />, title: "Hands-on Projects", text: "Stop watching passive videos and start building. Every module ends with a real-world product you can add to your portfolio." },
    { icon: <Users size={28} />, title: "Global Mentorship", text: "You are never learning alone. Get 24/7 support from peers and live feedback from expert mentors across the globe." }
  ];

  return (
    <Box sx={{ py: 24, bgcolor: "white", position: "relative" }}>
      <Container maxWidth="xl">
        <div className="text-center mb-16 max-w-3xl mx-auto">
           <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 bg-orange-50 rounded-full border border-orange-100 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#d15424] animate-pulse"></span>
              <span className="text-xs font-bold text-gray-700 font-montserrat uppercase tracking-wider">The E1X Advantage</span>
           </div>
          <Typography variant="h2" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2.5rem", md: "3.5rem" }, fontFamily: "Monserrat, sans-serif", letterSpacing: "-0.02em", lineHeight: 1.1, mb: 4 }}>
            Why top talents choose to learn with us.
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cards.map((card, i) => (
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15, duration: 0.6 }}
                    key={i} 
                    className="bg-[#FAFAFA] rounded-[2rem] p-10 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:border-gray-200 transition-all duration-500 group"
                >
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center text-[#d15424] mb-8 group-hover:scale-110 transition-transform duration-500 group-hover:bg-[#d15424] group-hover:text-white">
                        {card.icon}
                    </div>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mb: 4, fontFamily: "Monserrat, sans-serif", fontSize: "1.35rem" }}>{card.title}</Typography>
                    <Typography variant="body1" sx={{ color: "#475569", lineHeight: 1.7, fontFamily: "Monserrat, sans-serif", fontSize: "1rem" }}>{card.text}</Typography>
                </motion.div>
            ))}
        </div>
      </Container>
    </Box>
  );
};

export const WhoWeAreSection = () => {
  return (
    <Box sx={{ py: 24, bgcolor: "#FAFAFA", overflow: "hidden", borderTop: "1px solid #f1f5f9" }}>
        <Container maxWidth="xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                
                {/* Image Container */}
                <motion.div 
                   initial={{ opacity: 0, x: -40 }}
                   whileInView={{ opacity: 1, x: 0 }}
                   viewport={{ once: true }}
                   transition={{ duration: 0.8 }}
                   className="relative"
                >
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#d15424]/20 to-transparent rounded-[3rem] transform translate-x-6 translate-y-6 blur-xl"></div>
                    <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.1)] bg-white aspect-[4/3] border-[8px] border-white">
                        <img 
                            src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                            alt="E1X Academy Global Workspace" 
                            className="w-full h-full object-cover"
                        />
                    </div>
                    {/* Floating Premium Info */}
                    <div className="absolute -bottom-8 -right-8 bg-white/90 backdrop-blur-xl p-6 rounded-[1.5rem] shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-gray-100 flex items-center gap-5">
                        <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center text-green-600 border border-green-100">
                            <BookOpen size={24} />
                        </div>
                        <div>
                            <p className="font-black text-[#0f172a] font-montserrat text-2xl tracking-tight">95%</p>
                            <p className="text-sm text-gray-500 font-montserrat font-bold uppercase tracking-wider">Completion Rate</p>
                        </div>
                    </div>
                </motion.div>

                {/* Content */}
                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 bg-orange-50 rounded-full border border-orange-100 mb-6">
                        <span className="w-2 h-2 rounded-full bg-[#d15424] animate-pulse"></span>
                        <span className="text-xs font-bold text-gray-700 font-montserrat uppercase tracking-wider">Who We Are</span>
                    </div>
                    
                    <Typography variant="h2" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2.5rem", md: "3.5rem" }, mb: 6, lineHeight: 1.1, fontFamily: "Monserrat, sans-serif", letterSpacing: "-0.02em" }}>
                        Empowering the next generation of global tech leaders.
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#475569", mb: 8, lineHeight: 1.8, fontFamily: "Monserrat, sans-serif", fontSize: "1.15rem" }}>
                        E1X Academy is built on the belief that traditional education isn't keeping up with the fast-paced tech industry. We bridge the gap between abstract theory and practical application globally. When you graduate from E1X, you aren't just holding a certificate; you're holding a portfolio of real work.
                    </Typography>

                    <div className="flex flex-col gap-6 mb-10">
                        {["Led by Global Senior Engineers", "Accredited & Recognized Worldwide", "Guaranteed Portfolio Building"].map((item, i) => (
                            <div key={i} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                                <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center shrink-0">
                                    <CheckCircle className="text-[#d15424]" size={18} />
                                </div>
                                <span className="text-[#0f172a] font-bold font-montserrat">{item}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </Container>
    </Box>
  );
};

export const TestimonialSection = () => {
    const testimonials = [
        { name: "Sarah K.", role: "Frontend Engineer", company: "TechFlow London", text: "The live cohorts at E1X completely changed my trajectory. I went from struggling with basic JavaScript to landing my first global mid-level role." },
        { name: "Michael O.", role: "Data Scientist", company: "Finserve NYC", text: "Unlike other platforms, the capstone projects here are actually rigorous. I submitted my final E1X project during my interview and got hired on the spot." },
        { name: "Jessica T.", role: "Product Manager", company: "StartUp Inc Berlin", text: "The mentors are incredibly hands-on. If you put in the work, the global team here ensures you leave with undeniable skills." },
    ];

    return (
      <Box sx={{ py: 24, bgcolor: "white" }}>
        <Container maxWidth="xl">
          <div className="text-center mb-20 max-w-3xl mx-auto">
            <Typography variant="h2" sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: "2.5rem", md: "3.5rem" }, mb: 4, fontFamily: "Monserrat, sans-serif", letterSpacing: "-0.02em" }}>
              Don't just take our word for it.
            </Typography>
            <Typography variant="body1" sx={{ color: "#475569", fontFamily: "Monserrat, sans-serif", fontSize: "1.15rem", lineHeight: 1.6 }}>
              Thousands of students globally have used E1X to leapfrog their careers. Here is what our alumni have to say.
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t, i) => (
                  <motion.div 
                     initial={{ opacity: 0, y: 30 }}
                     whileInView={{ opacity: 1, y: 0 }}
                     viewport={{ once: true }}
                     transition={{ delay: i * 0.15, duration: 0.6 }}
                     key={i} 
                     className="bg-[#FAFAFA] rounded-[2rem] p-10 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.02)] relative hover:-translate-y-2 transition-transform duration-500"
                  >
                      {/* Quote Mark */}
                      <span className="absolute top-8 right-10 text-6xl text-gray-200 font-serif leading-none opacity-50">"</span>
                      
                      <div className="flex gap-1 mb-8">
                         {[1,2,3,4,5].map(s => <Star key={s} size={18} fill="#F59E0B" color="#F59E0B" />)}
                      </div>
                      <p className="text-[#334155] text-lg leading-relaxed font-montserrat mb-10 relative z-10 font-medium">
                          "{t.text}"
                      </p>
                      
                      <div className="flex items-center gap-4 mt-auto border-t border-gray-200 pt-6">
                          <div className="w-14 h-14 bg-gray-200 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-sm">
                               <img src={`https://i.pravatar.cc/150?img=${i+10}`} alt={t.name} className="w-full h-full object-cover" />
                          </div>
                          <div>
                              <p className="font-bold text-[#0f172a] text-base font-montserrat">{t.name}</p>
                              <p className="text-sm text-gray-500 font-montserrat mt-1">{t.role} @ <span className="text-[#d15424] font-bold">{t.company}</span></p>
                          </div>
                      </div>
                  </motion.div>
              ))}
          </div>
        </Container>
      </Box>
    );
};

export const NewsletterSection = () => {
    return (
        <Box sx={{ py: 0, pb: 20 }}>
            <Container maxWidth="lg">
                <div className="bg-[#111827] rounded-[3rem] p-10 md:p-20 relative overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.2)] flex flex-col md:flex-row items-center justify-between gap-12 border border-gray-800">
                    
                    {/* Decorative Premium Orbs */}
                    <div className="absolute -top-32 -left-32 w-[300px] h-[300px] bg-[#d15424] rounded-full blur-[100px] opacity-30 pointer-events-none"></div>
                    <div className="absolute -bottom-32 -right-32 w-[400px] h-[400px] bg-blue-600 rounded-full blur-[120px] opacity-20 pointer-events-none"></div>

                    <div className="relative z-10 md:w-1/2 text-center md:text-left">
                       <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10 mb-6">
                           <span className="text-xs font-bold text-gray-300 font-montserrat uppercase tracking-wider">Stay Connected</span>
                       </div>
                       <Typography variant="h3" sx={{ fontWeight: 900, color: "white", mb: 4, fontSize: { xs: "2.5rem", md: "3rem"}, fontFamily: "Monserrat, sans-serif", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                            Master the tech landscape.
                       </Typography>
                       <Typography variant="body1" sx={{ color: "#94a3b8", fontFamily: "Monserrat, sans-serif", fontSize: "1.15rem", lineHeight: 1.6 }}>
                            Subscribe to our global newsletter for exclusive tech resources, free masterclasses, and career advice. No spam, ever.
                       </Typography>
                    </div>

                    <div className="relative z-10 w-full md:w-[45%]">
                        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-2 flex items-center shadow-inner border border-white/10">
                            <input 
                                type="email" 
                                placeholder="Enter your email address..." 
                                className="w-full bg-transparent px-5 py-4 outline-none text-white placeholder-gray-400 font-montserrat"
                            />
                            <button className="bg-[#d15424] hover:bg-[#b8451d] text-white px-8 py-4 rounded-xl font-bold font-montserrat transition-colors whitespace-nowrap flex items-center gap-2 shadow-[0_4px_12px_rgba(209,84,36,0.3)]">
                                Subscribe <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </Container>
        </Box>
    )
}
